import { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const SearchableSelectField = ({
  label,
  required,
  value,
  onChange,
  options = [],
  error,
  placeholder = "Select an option",
  searchPlaceholder = "Search...",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef(null);

  const selectedOption = options.find(
    (option) => String(option.value) === String(value)
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return options;
    }

    return options.filter((option) =>
      String(option.label || "").toLowerCase().includes(query)
    );
  }, [options, search]);

  const selectOption = (optionValue) => {
    onChange(optionValue);
    setSearch("");
    setOpen(false);
  };

  return (
    <div className="min-w-0">
      {label && (
        <Label className="text-xs text-slate-600">
          {label}
          {required && <span className="text-rose-600 ml-1">*</span>}
        </Label>
      )}

      <Popover
        open={open}
        modal={false}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);

          if (!nextOpen) {
            setSearch("");
            return;
          }

          window.requestAnimationFrame(() => {
            searchInputRef.current?.focus();
          });
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={Boolean(error)}
            className={`mt-1 h-9 w-full min-w-0 justify-between rounded-md border border-input bg-white px-3 text-sm font-normal shadow-sm ring-offset-background hover:bg-white focus-visible:ring-1 focus-visible:ring-ring ${error ? "border-rose-500 focus-visible:ring-rose-500" : ""}`}
            data-testid={testId}
          >
            <span className="min-w-0 flex-1 truncate text-left">
              {selectedOption?.label || placeholder}
            </span>

            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-slate-500 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-[80] w-[min(520px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] p-2"
        >
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <Input
              ref={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9"
            />
          </div>

          <div
            className="max-h-[320px] overflow-y-auto overscroll-contain touch-pan-y pr-1"
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-slate-500">
                No options found.
              </div>
            ) : (
              filteredOptions.map((option) => {
                const selected = String(option.value) === String(value);

                return (
                  <button
                    type="button"
                    key={option.value}
                    onClick={() => selectOption(option.value)}
                    className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-slate-100"
                  >
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        selected ? "opacity-100" : "opacity-0"
                      }`}
                    />

                    <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug text-slate-700">
                      {option.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      {error && <div className="text-xs text-rose-600 mt-1">{error}</div>}
    </div>
  );
};
